import { useEffect, useRef, useState } from "react";

// ============ PROVOCATIVE. gedeelde basis (Fase 02) ============
// Zwarte/redactionele stijl volgens spec 04. Tweetalig: EN standaard, NL via schakelaar.

export const PROV = {
  bg: "#0A0A0A", primary: "#C1C1C1", secondary: "#858585", muted: "#666666",
  border: "#333333", highlight: "#BEBEBE",
};

const API = process.env.REACT_APP_BACKEND_URL;
// Rood PROVOKE-seriflogo (origineel) — header-lockup + sticky nav
export const PROVOKE_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/3035d3de803b1bac_Red%20Italic%20Serif%20PROVOKE%20Wordmark.png";
// PROVOCATIVE.-woordmerk (door gebruiker aangeleverd) — ALLEEN als hero-titel op de landing
export const PROV_LOGO = `${API}/api/provocative/public/logo.png`;
export const JOH_LOGO_WHITE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/626baa3c837517fb_Wit%20logo%20%E2%80%9CJOH.%E2%80%9D%20op%20transparante%20achtergrond.png";

export const PROV_MEDIA = {
  mp4: `${API}/api/provocative/public/bg-video.mp4`,
  webm: `${API}/api/provocative/public/bg-video.webm`,
  poster: `${API}/api/provocative/public/bg-poster.jpg`,
};
// Bevestigingsvideo na RSVP (door gebruiker aangeleverde clip)
export const PROV_RSVP_VIDEO = `${API}/api/provocative/public/rsvp-confirm.mp4`;
export const PROV_RSVP_VIDEO_WEBM = `${API}/api/provocative/public/rsvp-confirm.webm`;

// ---------- sessie-token (server-side sessies; remember → localStorage, anders sessionStorage) ----------
export function provToken() {
  return localStorage.getItem("prov_token") || sessionStorage.getItem("prov_token");
}
export function provAuth() {
  const t = provToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}
export function provSetToken(token, remember) {
  provClearToken();
  (remember ? localStorage : sessionStorage).setItem("prov_token", token);
}
export function provClearToken() {
  localStorage.removeItem("prov_token");
  sessionStorage.removeItem("prov_token");
}

// ---------- taal ----------
export function useProvLang() {
  const [lang, setLangState] = useState(() => (localStorage.getItem("prov_lang") === "nl" ? "nl" : "en"));
  const setLang = (l) => {
    localStorage.setItem("prov_lang", l);
    setLangState(l);
  };
  const t = (key) => STR[key]?.[lang] ?? key;
  return { lang, setLang, t };
}

export function LangSwitch({ lang, setLang }) {
  return (
    <div className="flex items-center gap-4" data-testid="prov-lang-switcher">
      {["en", "nl"].map((l) => (
        <button key={l} onClick={() => setLang(l)}
          className={`font-mono text-[10px] tracking-[0.25em] uppercase transition-colors ${lang === l ? "text-[#C1C1C1] border-b border-[#C1C1C1]" : "text-[#666666] hover:text-[#858585]"}`}
          data-testid={`prov-lang-${l}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

// ---------- gecombineerde logo-header: JOH. links, kleine × , rood PROVOKE-logo rechts (zoals eerder) ----------
export function LogoHeader({ compact = false }) {
  const joh = compact ? "h-8 md:h-9" : "h-12 md:h-14";
  const prov = compact ? "h-8 md:h-9" : "h-[72px] md:h-[84px]";
  return (
    <div className="flex items-center gap-3 md:gap-5" data-testid="prov-logo-header">
      <img src={JOH_LOGO_WHITE} alt="JOH." className={`${joh} w-auto max-w-[30vw] object-contain`} data-testid="prov-logo-joh" />
      <span className={`font-mono text-[#858585] ${compact ? "text-xs" : "text-xs"} select-none`} aria-hidden="true">×</span>
      <img src={PROVOKE_LOGO} alt="PROVOKE" className={`${prov} w-auto max-w-[40vw] object-contain`} data-testid="prov-logo-provoke" />
    </div>
  );
}

// ---------- sticky navigatiebalk (donker) met PROVOKE-logo — blijft zichtbaar bij scrollen ----------
// Sticky top-bar op alle PROVOCATIVE.-pagina's: officieel logo EXACT gecentreerd t.o.v.
// de viewport (niet de restruimte), navigatie rechts. Rode PROVOKE-logo zit hier bewust
// niet meer in (spec: header-branding = PROVOCATIVE. op auth + dashboard).
export function StickyNav({ children }) {
  return (
    <div className="sticky top-0 z-40 border-b backdrop-blur-sm" style={{ background: "rgba(10,10,10,0.92)", borderColor: PROV.border }} data-testid="prov-sticky-nav">
      <div className="relative flex items-center justify-between px-4 md:px-10 min-h-[60px] md:min-h-[72px]">
        <span className="w-10 shrink-0" aria-hidden="true" />
        <HeaderLogo />
        <div className="flex items-center gap-3 md:gap-6 shrink-0">{children}</div>
      </div>
    </div>
  );
}

// Dedicated header-logo — exacte maten volgens spec (desktop 160px / tablet 140px /
// mobile 120px, auto-hoogte m.b.v. originele verhoudingen; <360px iets compacter zodat
// logo en navigatie nooit overlappen). Bewust geen gedeelde CSS-klasse elders.
export function HeaderLogo() {
  return (
    <img src={PROV_LOGO} alt="PROVOCATIVE."
      className="prov-header-logo absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[104px] min-[360px]:w-[120px] md:w-[140px] lg:w-[160px] h-auto object-contain pointer-events-none"
      data-testid="prov-sticky-logo" />
  );
}

// ---------- fullscreen achtergrondvideo (autoplay/muted/loop, poster-fallback, reduced-motion) ----------
export function ProvBgVideo() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) ref.current?.pause();
  }, []);
  return (
    <>
      <video ref={ref} className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0 }}
        autoPlay muted loop playsInline preload="auto" poster={PROV_MEDIA.poster}
        aria-hidden="true" tabIndex={-1} data-testid="prov-bg-video">
        <source src={PROV_MEDIA.mp4} type="video/mp4" />
        <source src={PROV_MEDIA.webm} type="video/webm" />
      </video>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, background: "rgba(10,10,10,0.72)" }} aria-hidden="true" data-testid="prov-bg-overlay" />
    </>
  );
}

// ---------- teksten (EN standaard / NL) ----------
export const STR = {
  initiative: { en: "A JOH × PROVOKE INITIATIVE", nl: "EEN JOH × PROVOKE INITIATIEF" },
  phonetic: { en: "/prəˈvɒk.ə.tɪv/", nl: "/prəˈvɒk.ə.tɪv/" },
  pos: { en: "adjective", nl: "bijvoeglijk naamwoord" },
  enter: { en: "ENTER →", nl: "NAAR BINNEN →" },
  sign_in: { en: "SIGN IN", nl: "INLOGGEN" },
  register: { en: "REGISTER", nl: "REGISTREREN" },
  first_name: { en: "FIRST NAME", nl: "VOORNAAM" },
  last_name: { en: "LAST NAME", nl: "ACHTERNAAM" },
  email: { en: "EMAIL ADDRESS", nl: "E-MAILADRES" },
  gender: { en: "GENDER (OPTIONAL)", nl: "GENDER (OPTIONEEL)" },
  gender_male: { en: "MALE", nl: "MAN" },
  gender_female: { en: "FEMALE", nl: "VROUW" },
  gender_undisclosed: { en: "PREFER NOT TO SAY", nl: "ZEG IK LIEVER NIET" },
  send_code: { en: "SEND CODE →", nl: "STUUR CODE →" },
  code_sent_to: { en: "CODE SENT TO", nl: "CODE VERSTUURD NAAR" },
  code_label: { en: "6-DIGIT CODE", nl: "6-CIJFERIGE CODE" },
  remember_me: { en: "REMEMBER ME", nl: "ONTHOUD MIJ" },
  confirm: { en: "CONFIRM →", nl: "BEVESTIGEN →" },
  back: { en: "← BACK", nl: "← TERUG" },
  err_required: { en: "This field is required", nl: "Dit veld is verplicht" },
  err_email: { en: "Invalid email address", nl: "Ongeldig e-mailadres" },
  err_code_invalid: { en: "Invalid code — check your email and try again", nl: "Ongeldige code — controleer je e-mail en probeer opnieuw" },
  err_code_expired: { en: "Code expired — request a new one", nl: "Code verlopen — vraag een nieuwe aan" },
  err_code_locked: { en: "Too many attempts — request a new code", nl: "Te veel pogingen — vraag een nieuwe code aan" },
  err_throttle: { en: "Please wait a moment before requesting a new code", nl: "Wacht even voordat je een nieuwe code aanvraagt" },
  err_no_account: { en: "No registration found for this email — register first", nl: "Geen registratie gevonden voor dit e-mailadres — registreer eerst" },
  err_already: { en: "This email is already registered — sign in instead", nl: "Dit e-mailadres is al geregistreerd — log in" },
  err_generic: { en: "Something went wrong — try again", nl: "Er ging iets mis — probeer opnieuw" },
  pending_title: { en: "STATUS: AWAITING APPROVAL", nl: "STATUS: IN AFWACHTING VAN GOEDKEURING" },
  pending_body: {
    en: "Your registration has been received. Once approved, you will get access to your personal dashboard.",
    nl: "Je aanmelding is ontvangen. Zodra je bent goedgekeurd, krijg je toegang tot je persoonlijke dashboard.",
  },
  rejected_title: { en: "STATUS: NOT APPROVED", nl: "STATUS: NIET GOEDGEKEURD" },
  rejected_body: {
    en: "Your registration was not approved. Questions? Contact team@johboxing.com.",
    nl: "Je aanmelding is niet goedgekeurd. Vragen? Mail team@johboxing.com.",
  },
  next_session: { en: "NEXT SESSION", nl: "VOLGENDE SESSIE" },
  im_there: { en: "I'M THERE", nl: "IK BEN ERBIJ" },
  youre_in: { en: "YOU'RE IN", nl: "JE BENT ERBIJ" },
  confirm_skip: { en: "SKIP →", nl: "OVERSLAAN →" },
  confirm_replay: { en: "REPLAY", nl: "OPNIEUW" },
  use_password: { en: "USE EMAIL + PASSWORD", nl: "GEBRUIK E-MAIL + WACHTWOORD" },
  use_code: { en: "← USE EMAIL CODE", nl: "← GEBRUIK E-MAILCODE" },
  password: { en: "PASSWORD", nl: "WACHTWOORD" },
  forgot: { en: "FORGOT PASSWORD?", nl: "WACHTWOORD VERGETEN?" },
  forgot_sent: { en: "If this email is known, a reset link is on its way. The link is valid for 1 hour.", nl: "Als dit adres bekend is, ontvang je een resetlink per e-mail. De link is 1 uur geldig." },
  send_reset: { en: "SEND RESET LINK →", nl: "STUUR RESETLINK →" },
  err_invalid: { en: "Invalid email or password", nl: "Ongeldig e-mailadres of wachtwoord" },
  err_locked: { en: "Too many attempts — try again in 15 minutes", nl: "Te veel pogingen — probeer het over 15 minuten opnieuw" },
  err_no_member: { en: "No PROVOCATIVE. registration for this account — register first with an email code", nl: "Geen PROVOCATIVE.-registratie voor dit account — registreer eerst met een e-mailcode" },
  confirm_password: { en: "CONFIRM PASSWORD", nl: "BEVESTIG WACHTWOORD" },
  expect_title: { en: "WHAT TO EXPECT", nl: "WAT JE KUNT VERWACHTEN" },
  expect_intro: { en: "Never boxed before? No problem.\nEvery session is designed to challenge you at your own level. We'll guide you through the fundamentals of boxing, movement and conditioning in a supportive environment.", nl: "Nog nooit gebokst? Geen probleem.\nElke training is ontworpen om je op jouw eigen niveau uit te dagen. We begeleiden je door de fundamenten van boksen, beweging en conditie in een ondersteunende omgeving." },
  expect_1t: { en: "ALL LEVELS WELCOME.", nl: "ALLE NIVEAUS WELKOM." },
  expect_1d: { en: "No previous boxing experience required.", nl: "Geen bokservaring vereist." },
  expect_2t: { en: "60 MINUTES.", nl: "60 MINUTEN." },
  expect_2d: { en: "Warm-up, technique, boxing drills and cooldown.", nl: "Warming-up, techniek, boksdrills en cooling-down." },
  expect_3t: { en: "YOUR PACE. YOUR PROGRESS.", nl: "JOUW TEMPO. JOUW PROGRESSIE." },
  expect_3d: { en: "Focus on learning, improving and enjoying the process.", nl: "Focus op leren, verbeteren en genieten van het proces." },
  expect_closing: { en: "COME AS YOU ARE. LEAVE STRONGER.", nl: "KOM ZOALS JE BENT. VERTREK STERKER." },
  err_pw_mismatch: { en: "Passwords do not match", nl: "Wachtwoorden komen niet overeen" },
  err_pw_short: { en: "Password must be at least 8 characters", nl: "Wachtwoord moet minimaal 8 tekens zijn" },
  err_email_taken: { en: "An account with this email already exists — sign in instead", nl: "Er bestaat al een account met dit e-mailadres — log in" },
  cancel_rsvp: { en: "CANCEL", nl: "ANNULEER" },
  also_scheduled: { en: "ALSO SCHEDULED", nl: "OOK INGEPLAND" },
  your_progress: { en: "YOUR PROGRESS", nl: "JOUW VOORTGANG" },
  sessions_attended: { en: "SESSIONS ATTENDED", nl: "SESSIES BIJGEWOOND" },
  what_to_bring: { en: "WHAT TO BRING", nl: "WAT NEEM JE MEE" },
  gear_gloves: { en: "Boxing gloves", nl: "Bokshandschoenen" },
  gear_wraps: { en: "Hand wraps / Bandages", nl: "Hand wraps / Bandages" },
  gear_towel: { en: "Towel", nl: "Handdoek" },
  gear_water: { en: "Water bottle", nl: "Bidon" },
  gear_groin: { en: "Groin protector (if applicable)", nl: "Kruisbeschermer (indien van toepassing)" },
  gear_bra: { en: "Sports bra (if applicable)", nl: "Sportbeha (indien van toepassing)" },
  gear_mouthguard: { en: "Mouthguard (for contact drills)", nl: "Gebitsbeschermer (bij contactoefeningen)" },
  gear_missing_q: { en: "MISSING EQUIPMENT?", nl: "MIST JE MATERIAAL?" },
  gear_missing_hint: {
    en: "Let us know in advance if you're missing any of the items above — tick what you don't have.",
    nl: "Laat het ons vooraf weten als je iets van de lijst mist — vink aan wat je niet hebt.",
  },
  gear_report: { en: "REPORT MISSING ITEMS", nl: "MELD MISSENDE ITEMS" },
  gear_confirm_all: { en: "I HAVE EVERYTHING", nl: "IK HEB ALLES" },
  gear_clear: { en: "CLEAR REPORT", nl: "MELDING WISSEN" },
  gear_state_reported: { en: "MISSING EQUIPMENT REPORTED", nl: "MISSEND MATERIAAL GEMELD" },
  gear_state_ok: { en: "EQUIPMENT CONFIRMED AVAILABLE", nl: "MATERIAAL BEVESTIGD AANWEZIG" },
  gear_state_none: { en: "CHECKLIST NOT COMPLETED", nl: "CHECKLIST NOG NIET INGEVULD" },
  session_history: { en: "SESSION HISTORY", nl: "SESSIEHISTORIE" },
  history_empty: { en: "No previous sessions yet.", nl: "Nog geen eerdere sessies." },
  history_confirmed: { en: "CONFIRMED", nl: "AANGEMELD" },
  history_cancelled: { en: "CANCELLED", nl: "GEANNULEERD" },
  logout: { en: "LOG OUT", nl: "UITLOGGEN" },
  weekly: { en: "WEEKLY TRAINING", nl: "WEKELIJKSE TRAINING" },
  location: { en: "LOCATION", nl: "LOCATIE" },
  days_en: { en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], nl: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"] },
  months_en: { en: ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"], nl: ["JANUARI", "FEBRUARI", "MAART", "APRIL", "MEI", "JUNI", "JULI", "AUGUSTUS", "SEPTEMBER", "OKTOBER", "NOVEMBER", "DECEMBER"] },
};

export function fmtSessionDate(isoDate, weekday, lang) {
  const d = new Date(`${isoDate}T00:00:00`);
  const day = STR.days_en[lang][weekday];
  const month = STR.months_en[lang][d.getMonth()];
  return `${day} ${d.getDate()} ${month} ${d.getFullYear()}`;
}
